import { HotspotPinComponent as HotspotPincomponentType } from '@lessonup/pins-shared';
import { styled } from '@lessonup/ui-components';
import React from 'react';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { isBoxLayout } from '../../types';
import { computeOpacity, computeShadowFilter } from '../../utils';
import {
  getPinComponentBackgroundColor,
  getPinComponentBorderColor,
  getPinComponentInnerColor,
} from '../../utils/settings';
import { DEFAULT_BORDER_SIZE, getHotspotIcon } from './hotspot.utils';
import { HotspotContentHolder } from './HotspotContentHolder';

export interface HotspotComponentProps {
  pinId: string;
  pinComponent: HotspotPincomponentType;
  active: boolean;
  Renderer?: TipTapComponent;
}

// These are based how these sizes are relative to the hotspot size in the Figma design
const ICON_SIZE = '57%';
const FONT_SIZE_FACTOR = 18 / 60;

export const HotspotPinComponent: React.FC<HotspotComponentProps> = ({ pinId, pinComponent, active, Renderer }) => {
  const hotspotPosition = isBoxLayout(pinComponent.layout) ? pinComponent.layout.position : { x: 0, y: 0 };
  const hotspotSize = isBoxLayout(pinComponent.layout) ? pinComponent.layout.size : { width: 0, height: 0 };
  const borderSize = pinComponent.settings.borderSize ?? DEFAULT_BORDER_SIZE;
  const fillType = pinComponent.settings.fillType;
  const fontSize = hotspotSize.height * FONT_SIZE_FACTOR;

  return (
    <>
      <HotspotContentHolder
        showBody={pinComponent.settings.showBody || active}
        bodyText={pinComponent.settings.body || undefined}
        title={pinComponent.settings.title || undefined}
        hotspotSize={hotspotSize}
        hotspotPosition={hotspotPosition}
        expandDirection={pinComponent.settings.expandDirection}
        backgroundColor={pinComponent.settings.contentBackgroundColor}
        textColor={pinComponent.settings.contentTextColor}
        shadowSetting={pinComponent.settings.shadow}
        pinId={pinId}
        componentId={pinComponent.id}
        Renderer={Renderer}
      />
      <StyledHotspotWrapper
        style={{
          borderWidth: `${borderSize}px`,
          borderColor: getPinComponentBorderColor(pinComponent),
          backgroundColor: getPinComponentBackgroundColor(pinComponent),
          color: getPinComponentInnerColor(pinComponent),
          opacity: computeOpacity(pinComponent.settings.transparency),
          boxShadow: computeShadowFilter(pinComponent.settings.shadow),
          borderRadius: '100%',
        }}
      >
        {fillType === 'ICON' && getHotspotIcon(pinComponent.settings.iconFill)?.icon}

        {fillType === 'IMAGE' && (
          <StyledImageCore style={{ backgroundImage: `url(${pinComponent.settings.imageFill.url})` }} />
        )}
        {fillType === 'TEXT' && (
          <StyledTextWrapper>
            <StyledTextCore
              style={{
                fontSize: fontSize,
              }}
            >
              {pinComponent.settings.textFill}
            </StyledTextCore>
          </StyledTextWrapper>
        )}
      </StyledHotspotWrapper>
    </>
  );
};

const StyledHotspotWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  border-style: solid;
  cursor: pointer;

  svg {
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
  }
`;

const StyledTextWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextCore = styled.div`
  user-select: none;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledImageCore = styled.div`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;
